import React, { ReactNode } from 'react';

import { EvError, EvSection, IEvTabPanelProps, IEvTabProps } from '@evinced-private/ui-common';

import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { TableSkeleton } from 'src/common/components/skeletons/table-skeleton/TableSkeleton';

import {
	ISSUES_PAGE_TABS_ID,
	ISSUES_TAB_IDS,
	ISSUES_TABLE_SKELETON_ROW_COUNT
} from '../IssuesPage.constants';
import { ISSUES_PAGE_LABELS } from '../IssuesPage.labels';
import {
	BuildIssuesPageTabsParams,
	IssuesPageTabsResult,
	RenderIssuesTableContentParams
} from '../IssuesPage.types';

export const renderIssuesTableContent = ({
	columns,
	isFetching,
	error,
	data,
	tableId,
	title,
	errorMessage
}: RenderIssuesTableContentParams): ReactNode => {
	if (isFetching) {
		return <TableSkeleton columns={columns.length} rowCount={ISSUES_TABLE_SKELETON_ROW_COUNT} />;
	}
	if (error) {
		return <EvError error={errorMessage} />;
	}
	return (
		<PlatformTable
			tableId={tableId}
			columns={columns}
			tableData={data?.issues || []}
			totalCount={data?.unfilteredTotal || 0}
			totalPaginationableResults={data?.totalCount || 0}
			persistTableState
			options={{
				keyField: 'id',
				pagination: true,
				dataType: 'issues',
				title,
				caption: `${title} Table`,
				remote: true
			}}
		/>
	);
};

const buildIssuesTab = (
	id: string,
	title: string,
	dataAutomationId: string,
	isActive: boolean,
	onActivate: () => void
): IEvTabProps => ({
	id: `${id}-tab`,
	tablistId: ISSUES_PAGE_TABS_ID,
	ref: null,
	isActive,
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	handleKeyDown: () => {},
	children: title,
	ariaControls: `${id}-panel`,
	onActivate,
	dataAutomationId
});

const buildIssuesTabPanel = (
	id: string,
	ariaLabel: string,
	isActive: boolean,
	content: ReactNode
): IEvTabPanelProps => ({
	id: `${id}-panel`,
	ariaLabelledBy: `${ISSUES_PAGE_TABS_ID}-${id}-tab`,
	isActive,
	children: (
		<EvSection ariaLabel={ariaLabel}>
			<div className="issues-table-container">{content}</div>
		</EvSection>
	)
});

export const buildIssuesPageTabs = ({
	activeTabIndex,
	onTabSelected,
	allTabContent,
	uniqueTabContent
}: BuildIssuesPageTabsParams): IssuesPageTabsResult => ({
	tabs: [
		buildIssuesTab(
			ISSUES_TAB_IDS.ALL,
			ISSUES_PAGE_LABELS.ALL_ISSUES_TAB,
			'all-issues-tab',
			activeTabIndex === 0,
			() => onTabSelected(0)
		),
		buildIssuesTab(
			ISSUES_TAB_IDS.UNIQUE,
			ISSUES_PAGE_LABELS.UNIQUE_ISSUES_TAB,
			'unique-issues-tab',
			activeTabIndex === 1,
			() => onTabSelected(1)
		)
	],
	tabPanels: [
		buildIssuesTabPanel(
			ISSUES_TAB_IDS.ALL,
			ISSUES_PAGE_LABELS.ALL_ISSUES_TABLE_ARIA_LABEL,
			activeTabIndex === 0,
			allTabContent
		),
		buildIssuesTabPanel(
			ISSUES_TAB_IDS.UNIQUE,
			ISSUES_PAGE_LABELS.UNIQUE_ISSUES_TABLE_ARIA_LABEL,
			activeTabIndex === 1,
			uniqueTabContent
		)
	]
});
